import { type ReactNode, useMemo, useState } from 'react';

export type StepParams = {
  currentStep: number;
  stepsCount: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  setCurrentStep: (step: number) => void;
  goToPrevStep: () => void;
  goToNextStep: () => void;
};

export type UseStepper = StepParams & {
  stepContent: ReactNode;
};

export function useStepper(steps: ((stepContent: StepParams) => ReactNode)[]): UseStepper {
  const [currentStep, setCurrentStep] = useState(0);

  const stepParams = useMemo(
    (): StepParams => ({
      currentStep,
      stepsCount: steps.length,
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === steps.length - 1,
      setCurrentStep,
      goToPrevStep: () => setCurrentStep((currentStep) => (currentStep > 0 ? currentStep - 1 : currentStep)),
      goToNextStep: () =>
        setCurrentStep((currentStep) => (currentStep < steps.length - 1 ? currentStep + 1 : currentStep)),
    }),
    [currentStep, steps.length]
  );

  const stepContent = useMemo(() => {
    return steps[currentStep](stepParams);
  }, [currentStep, stepParams, steps]);

  return {
    ...stepParams,
    stepContent,
  };
}
